/**
 * CUSTOMER-172B — Search shell: native input island + sibling suggestion surface.
 *
 * HARD CONTRACT:
 * - No React setState on beforeinput/input/keydown frame for the input island.
 * - No visualViewport → React state during keyboard animation.
 * - Suggest only after paint + debounce; AbortController / requestId gated.
 * - Input never remounted via key= tied to query/results/revision.
 */
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { SearchSuggestion } from "./search-v2.js";

/** After-paint only. Must not import search-v2 runtime (omnisearch warm). */
function compactLen(q: string): number {
  return String(q ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[\s\-_'’.,:;!?]+/g, "").length;
}
import { scheduleSuggest } from "./search-suggest-runtime-v2.js";
import { markSearchPerf } from "./search-perf-marks-v2.js";
import {
  clearSearchDraft,
  commitSubmittedQuery,
  setSearchDraftQuery,
  setSearchSuggestionQuery,
} from "./search-session-v2.js";
import {
  SearchNativeInputIsland,
  type SearchNativeInputHandlers,
} from "./SearchNativeInputIsland.js";
import { SearchSuggestionSurface } from "./SearchSuggestionSurface.js";
import {
  getFocusInputTraceSummary,
  isSearchPerfDiagEnabled,
} from "./search-focus-trace-v2.js";
import { emitEvent } from "./events-v2.js";

type Props = {
  lang: StoreV2Lang;
  onSubmit: (query: string) => void;
  onOpenProduct?: (
    productId: string,
    seed?: { id: string; title?: string; imageUrl?: string | null; priceUah?: number | null },
  ) => void;
  onOpenBrand?: (brandName: string) => void;
  onOpenCategoryLabel?: (categoryName: string) => void;
  visualSlot?: ReactNode;
  onPerf?: (metric: string, ms: number, extra?: Record<string, number>) => void;
  forceCloseToken?: number;
  sessionEpoch?: number;
  /** COMMERCE-176C-R3 — collection-scoped suggest (promotions); never hits global catalog. */
  localSuggest?: (query: string) => SearchSuggestion[];
};

const SUGGEST_DEBOUNCE_MS = 180;
const MAX_SUGGESTIONS = 5;

type OverlayReason =
  | "close_submit"
  | "close_select"
  | "close_blur"
  | "close_escape"
  | "close_scroll"
  | "close_route"
  | "close_clear"
  | "close_short"
  | "close_browsing";

function IsolatedSearchBarInner({
  lang,
  onSubmit,
  onOpenProduct,
  onOpenBrand,
  onOpenCategoryLabel,
  visualSlot,
  onPerf,
  forceCloseToken = 0,
  sessionEpoch = 0,
  localSuggest,
}: Props) {
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const mountCountRef = useRef(0);
  const pickingSuggestionRef = useRef(false);
  const browsingLockedRef = useRef(false);
  const submittedRef = useRef(false);
  const focusedRef = useRef(false);
  const suggestSeqRef = useRef(0);
  const requestCountRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelSuggestRef = useRef<(() => void) | null>(null);
  const lastDraftRef = useRef("");
  const listenerAuditRef = useRef({ scroll: 0, touchmove: 0 });
  /** Per-island session — never the global search-session singleton. */
  const islandSessionRef = useRef(`isl_${Math.random().toString(36).slice(2, 10)}`);
  const lastForceCloseRef = useRef(forceCloseToken);

  const closeOverlay = useCallback((reason: OverlayReason) => {
    suggestSeqRef.current += 1;
    cancelSuggestRef.current?.();
    cancelSuggestRef.current = null;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    // Skip React updates when already closed (critical for short-query path).
    setSuggestOpen((open) => {
      if (!open && reason === "close_short") return false;
      return false;
    });
    setSuggestLoading(false);
    setSuggestions((prev) => (prev.length === 0 ? prev : []));
    // close_route must NOT lock typing — remount/token churn after navigation
    // previously set browsingLocked and then rejected valid suggest responses.
    if (
      reason === "close_scroll" ||
      reason === "close_browsing" ||
      reason === "close_submit" ||
      reason === "close_select"
    ) {
      browsingLockedRef.current = true;
    }
    onPerf?.("suggest_close", 0, { reason_code: reason.length });
  }, [onPerf]);

  useEffect(() => {
    if (forceCloseToken > lastForceCloseRef.current) {
      lastForceCloseRef.current = forceCloseToken;
      closeOverlay("close_route");
    }
  }, [forceCloseToken, closeOverlay]);

  useEffect(() => {
    if (sessionEpoch <= 0) return;
    submittedRef.current = false;
    browsingLockedRef.current = false;
    clearSearchDraft();
    lastDraftRef.current = "";
    suggestSeqRef.current += 1;
    cancelSuggestRef.current?.();
    cancelSuggestRef.current = null;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSuggestions([]);
    setSuggestOpen(false);
    setSuggestLoading(false);
  }, [sessionEpoch]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelSuggestRef.current?.();
    },
    [],
  );

  // Scroll/touch close — ONLY while overlay open. NO visualViewport → React.
  useEffect(() => {
    if (!suggestOpen) return;
    const scrollRoot =
      document.querySelector<HTMLElement>("[data-store-scroll-root]") ??
      document.querySelector<HTMLElement>(".sv2-root");

    const onScroll = (ev: Event) => {
      const t = ev.target as Node | null;
      if (wrapRef.current && t && wrapRef.current.contains(t)) return;
      closeOverlay("close_scroll");
    };
    const onTouchMove = (ev: TouchEvent) => {
      const t = ev.target as Node | null;
      if (wrapRef.current && t && wrapRef.current.contains(t)) return;
      if (wrapRef.current && t && !wrapRef.current.contains(t)) {
        closeOverlay("close_browsing");
      }
    };

    listenerAuditRef.current.scroll += 1;
    listenerAuditRef.current.touchmove += 1;
    if (isSearchPerfDiagEnabled()) {
      (window as Window & { __AS_SEARCH_LISTENER_AUDIT__?: typeof listenerAuditRef.current }).__AS_SEARCH_LISTENER_AUDIT__ =
        { ...listenerAuditRef.current };
    }
    scrollRoot?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("touchmove", onTouchMove, { passive: true, capture: true });
    return () => {
      listenerAuditRef.current.scroll -= 1;
      listenerAuditRef.current.touchmove -= 1;
      if (isSearchPerfDiagEnabled()) {
        (window as Window & { __AS_SEARCH_LISTENER_AUDIT__?: typeof listenerAuditRef.current }).__AS_SEARCH_LISTENER_AUDIT__ =
          { ...listenerAuditRef.current };
      }
      scrollRoot?.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("touchmove", onTouchMove, true);
    };
  }, [suggestOpen, closeOverlay]);

  const runSuggest = useCallback(
    (q: string) => {
      if (browsingLockedRef.current || submittedRef.current || !focusedRef.current) return;
      const seq = ++suggestSeqRef.current;
      const sessionId = islandSessionRef.current;
      const normalizedLen = compactLen(q);
      if (normalizedLen < 2) {
        // Soft-close without forcing work if already closed.
        cancelSuggestRef.current?.();
        cancelSuggestRef.current = null;
        setSuggestOpen((o) => (o ? false : o));
        setSuggestLoading(false);
        setSuggestions((p) => (p.length ? [] : p));
        return;
      }
      setSearchSuggestionQuery(q);
      setSuggestLoading(true);
      markSearchPerf("suggestion_request_start", { seq });
      cancelSuggestRef.current?.();
      if (localSuggest) {
        const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
        const rows = localSuggest(q).filter((row) => row.matchReason !== "exact_token");
        const computeMs =
          (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0;
        if (seq !== suggestSeqRef.current) return;
        if (!focusedRef.current || submittedRef.current) return;
        if (islandSessionRef.current !== sessionId) return;
        markSearchPerf("suggestion_response", { seq, computeMs });
        onPerf?.("suggest_compute_ms", computeMs, {
          text_search_request_count: requestCountRef.current,
          suggest_count: rows.length,
        });
        setSuggestions(rows);
        setSuggestLoading(false);
        setSuggestOpen(true);
        requestAnimationFrame(() => markSearchPerf("suggestion_painted", { seq }));
        return;
      }
      cancelSuggestRef.current = scheduleSuggest(null, { seq, query: q, limit: MAX_SUGGESTIONS }, (res) => {
        if (res.seq !== suggestSeqRef.current) return;
        if (!focusedRef.current || submittedRef.current) return;
        if (islandSessionRef.current !== sessionId) return;
        if (compactLen(lastDraftRef.current) !== compactLen(q) || lastDraftRef.current !== q) return;
        // CUSTOMER-R4.15 — SEARCH_ERROR_SEMANTICS: network fail ≠ intentional empty suggest.
        if (res.failed) {
          setSuggestLoading(false);
          setSuggestOpen(false);
          return;
        }
        markSearchPerf("suggestion_response", { seq: res.seq, computeMs: res.computeMs });
        onPerf?.("suggest_compute_ms", res.computeMs, {
          text_search_request_count: requestCountRef.current,
          suggest_count: res.rows.length,
        });
        setSuggestions(res.rows);
        setSuggestLoading(false);
        setSuggestOpen(true);
        requestAnimationFrame(() => markSearchPerf("suggestion_painted", { seq: res.seq }));
      });
    },
    [onPerf, localSuggest],
  );

  const onDraftAfterPaint = useCallback(
    (v: string) => {
      lastDraftRef.current = v;
      // Module session only — not React state.
      setSearchDraftQuery(v);
      browsingLockedRef.current = false;
      submittedRef.current = false;
      markSearchPerf("search_input_event");

      if (debounceRef.current) clearTimeout(debounceRef.current);
      const len = compactLen(v);
      if (len < 2) {
        // NO sync closeOverlay from input frame — already past paint here, but still avoid churn.
        cancelSuggestRef.current?.();
        cancelSuggestRef.current = null;
        setSuggestOpen((o) => (o ? false : o));
        setSuggestLoading(false);
        setSuggestions((p) => (p.length ? [] : p));
        return;
      }
      // COMMERCE-176B-R2-R1 — show «Шукаємо…» within ~100ms (before debounced network).
      setSuggestOpen(true);
      setSuggestLoading(true);
      debounceRef.current = setTimeout(() => {
        if (browsingLockedRef.current || !focusedRef.current) return;
        if (lastDraftRef.current !== v) return;
        requestCountRef.current += 1;
        onPerf?.("text_search_request_count", requestCountRef.current);
        runSuggest(v);
      }, SUGGEST_DEBOUNCE_MS);

      if (isSearchPerfDiagEnabled()) {
        const summary = getFocusInputTraceSummary();
        onPerf?.("first_key_to_paint_ms", summary.first_key_to_paint_ms ?? -1, {
          long_task_count: summary.long_task_count,
          max_long_task_ms: summary.max_long_task_ms,
          input_mount_count: mountCountRef.current,
          scroll_listeners: listenerAuditRef.current.scroll,
        });
      }
    },
    [onPerf, runSuggest],
  );

  const submit = useCallback(
    (raw?: string) => {
      const q = String(raw ?? inputRef.current?.value ?? "").trim();
      if (compactLen(q) < 2) return;
      submittedRef.current = true;
      browsingLockedRef.current = true;
      commitSubmittedQuery(q);
      markSearchPerf("search_submit");
      closeOverlay("close_submit");
      if (inputRef.current) inputRef.current.value = "";
      lastDraftRef.current = "";
      clearSearchDraft();
      requestCountRef.current += 1;
      onPerf?.("text_search_request_count", requestCountRef.current);
      onSubmit(q);
    },
    [onSubmit, onPerf, closeOverlay],
  );

  const selectSuggestion = useCallback(
    (s: SearchSuggestion) => {
      pickingSuggestionRef.current = false;
      browsingLockedRef.current = true;
      closeOverlay("close_select");
      if (inputRef.current) inputRef.current.value = "";
      lastDraftRef.current = "";
      clearSearchDraft();

      if ((s.kind === "product" || s.kind === "sku") && s.productId && onOpenProduct) {
        emitEvent("search_suggestion_clicked", {
          kind: s.kind,
          product_id: s.productId,
          query: s.query,
        });
        onOpenProduct(s.productId, {
          id: s.productId,
          title: s.label,
          imageUrl: s.imageUrl ?? null,
          priceUah: s.priceUah ?? null,
        });
        return;
      }
      if (s.kind === "brand" && onOpenBrand) {
        onOpenBrand(s.query);
        return;
      }
      if (s.kind === "category" && onOpenCategoryLabel) {
        onOpenCategoryLabel(s.query);
        return;
      }
      submit(s.query);
    },
    [closeOverlay, onOpenProduct, onOpenBrand, onOpenCategoryLabel, submit],
  );

  // Keep aria-expanded off React props of the island (avoids island re-render on suggest open).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.setAttribute("aria-expanded", suggestOpen ? "true" : "false");
  }, [suggestOpen]);

  const handlersRef = useRef<SearchNativeInputHandlers>({
    onDraftAfterPaint,
    onSubmit: submit,
    onFocus: () => {
      focusedRef.current = true;
      submittedRef.current = false;
      browsingLockedRef.current = false;
      markSearchPerf("search_input_event", { focused: true });
    },
    onBlur: () => {
      focusedRef.current = false;
      window.setTimeout(() => {
        if (pickingSuggestionRef.current) return;
        closeOverlay("close_blur");
      }, 180);
    },
    onEscape: () => closeOverlay("close_escape"),
  });
  handlersRef.current.onDraftAfterPaint = onDraftAfterPaint;
  handlersRef.current.onSubmit = submit;
  handlersRef.current.onEscape = () => closeOverlay("close_escape");
  handlersRef.current.onFocus = () => {
    focusedRef.current = true;
    submittedRef.current = false;
    browsingLockedRef.current = false;
    markSearchPerf("search_input_event", { focused: true });
  };
  handlersRef.current.onBlur = () => {
    focusedRef.current = false;
    window.setTimeout(() => {
      if (pickingSuggestionRef.current) return;
      closeOverlay("close_blur");
    }, 180);
  };

  const search = (
    <div
      ref={wrapRef}
      className="sv2-search sv2-search-with-suggest"
      data-isolated-search="1"
      data-search-perf-172b="1"
      data-island-session={islandSessionRef.current}
      data-session-epoch={sessionEpoch}
      data-input-mount-count={mountCountRef.current}
    >
      <SearchNativeInputIsland
        lang={lang}
        sessionEpoch={sessionEpoch}
        handlersRef={handlersRef}
        mountCountRef={mountCountRef}
        inputRef={inputRef}
      />
      <button type="button" onClick={() => submit()}>
        {lang === "en" ? "Search" : "Пошук"}
      </button>
      <SearchSuggestionSurface
        lang={lang}
        open={suggestOpen && !submittedRef.current}
        loading={suggestLoading}
        suggestions={suggestions}
        onPick={selectSuggestion}
        onPickPointerDown={() => {
          pickingSuggestionRef.current = true;
        }}
        onShowAll={() => {
          const q = String(lastDraftRef.current || inputRef.current?.value || "").trim();
          if (q) {
            emitEvent("search_results_viewed", { query: q, source: "suggest_show_all" });
            submit(q);
          }
        }}
      />
    </div>
  );

  if (!visualSlot) return search;
  return (
    <div className="sv2-search-row">
      {search}
      {visualSlot}
    </div>
  );
}

export const IsolatedSearchBar = memo(IsolatedSearchBarInner);
